import React, { useMemo, EventHandler, KeyboardEvent, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { TableCell, TableRow } from '@material-ui/core'
import { useTranslation } from 'react-i18next'

import { ReactComponent as DropdownSVG } from '@percent/admin-dashboard/common/assets/images/dropdown.svg'
import { getDate } from '@percent/admin-dashboard/common/utility/getDate'
import { ErrorView, SearchAndDateFilter, Table, TimeLeft } from '@percent/admin-dashboard/common/components'
import { TableColumn, TableSort } from '@percent/admin-dashboard/common/components/table/Table.types'
import { CountryCodeFlag } from '@percent/admin-dashboard/common/components/countryCodeFlag/CountryCodeFlag'
import styles from './EligibilityTable.module.scss'
import { EligibilityStatusFilter, EligibilityTableProps } from './EligibilityTable.types'
import { handleClickAsLink } from '@percent/admin-dashboard/common/utility/handleClickAsLink'

const sortMapTableNamesToProperties = { requestDate: 'id', timeLeft: 'id' }

export function EligibilityTable({
  isLoading,
  subscriptions,
  totalResults,
  nextPage,
  previousPage,
  errorMessage,
  queryParams,
  setQueryParams
}: EligibilityTableProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const tabs = useMemo(() => [t('table.pending'), t('table.cantDefine'), t('table.live'), t('table.cancelled')], [t])

  const columns = useMemo(() => {
    const commonColumns: Array<TableColumn> = [
      { id: 'country', isSortable: false },
      { id: 'organization', isSortable: false },
      { id: 'partner', isSortable: false },
      { id: 'requestDate', isSortable: true, props: { width: '120px' } }
    ]

    if (queryParams.status === 'pending') {
      commonColumns.push({ id: 'timeLeft', isSortable: true })
    }

    return commonColumns
  }, [queryParams.status])

  const mapTableHeaderToProperty = (tableHeaderName: string) =>
    Object.keys(sortMapTableNamesToProperties).includes(tableHeaderName)
      ? sortMapTableNamesToProperties[tableHeaderName as keyof typeof sortMapTableNamesToProperties]
      : null

  const mapPropertyToTableHeader = (propertyName: string | null | undefined) => {
    return Object.keys(sortMapTableNamesToProperties).find(
      key => sortMapTableNamesToProperties[key as keyof typeof sortMapTableNamesToProperties] === propertyName
    )
  }

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) =>
    setQueryParams({
      direction: queryParams.direction === 'ASC' ? 'DESC' : 'ASC',
      sort: mapTableHeaderToProperty(property)
    })
  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    const status = EligibilityStatusFilter[newValue]

    if (status === 'pending') {
      setQueryParams({ status, sort: 'id', direction: 'ASC' })
    } else {
      setQueryParams({ status, sort: undefined, direction: undefined })
    }
  }

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(
    event => {
      if (event.key === 'Enter') {
        setQueryParams({
          query: (event.target as HTMLInputElement).value
        })
      }
    },
    [setQueryParams]
  )

  const filters = useMemo(
    () => (
      <SearchAndDateFilter
        handleKeyPress={handleKeyPress}
        maxLength={255}
        searchPhrase={queryParams.query ?? ''}
        placeholder={t('table.eligibilitySearchPlaceholder')}
        setQueryParams={setQueryParams}
      />
    ),
    [handleKeyPress, queryParams.query, t, setQueryParams]
  )

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <Table
      isLoading={isLoading}
      totalResults={totalResults}
      data={subscriptions}
      value={
        EligibilityStatusFilter[queryParams.status as keyof typeof EligibilityStatusFilter] ??
        EligibilityStatusFilter.pending
      }
      handleChange={handleTabChange}
      nextPage={nextPage}
      previousPage={previousPage}
      columns={columns}
      tabs={tabs}
      order={(queryParams.direction?.toLowerCase() as TableSort) ?? 'asc'}
      orderBy={mapPropertyToTableHeader(queryParams.sort)}
      handleRequestSort={handleRequestSort}
      emptyTableText={t('table.emptyEligibilitySubscriptions')}
      filtersContent={filters}
    >
      {subscriptions?.map(subscription => (
        <TableRow
          key={subscription.id}
          className={subscription.organisationId ? styles.row : undefined}
          onClick={
            subscription.organisationId ? handleClickAsLink(`/eligibility/${subscription.id}`, navigate) : undefined
          }
        >
          <TableCell>
            <CountryCodeFlag countryCode={subscription.countryCode} />
          </TableCell>
          <TableCell>{subscription.organisationName}</TableCell>
          <TableCell>{subscription.partnerName}</TableCell>
          <TableCell align="right" style={{ padding: '16px 50px 16px 0', textAlign: 'left', whiteSpace: 'nowrap' }}>
            {getDate(subscription.createdAt)}
          </TableCell>
          {queryParams.status === 'pending' && (
            <TableCell className={styles.nameRow}>
              <TimeLeft requestedDate={subscription.createdAt} />
            </TableCell>
          )}
          {subscription.id && (
            <TableCell align="right" style={{ padding: '16px 0' }} className={styles.rowIconForward}>
              <DropdownSVG className={styles.arrow} />
            </TableCell>
          )}
        </TableRow>
      ))}
    </Table>
  )
}
